import * as XLSX from 'xlsx';
import { ImportResultRow, ImportRowInput } from '../../shared/services/turnoGeo/types';

/** Encabezado del Excel (sin tildes, mayúsculas, sin espacios dobles) → campo. Acepta varios sinónimos. */
const HEADER_MAP: Record<string, keyof ImportRowInput> = {
  ITEM: 'item',
  CC: 'documentNumber',
  CEDULA: 'documentNumber',
  DOCUMENTO: 'documentNumber',
  PROCESO: 'process',
  'NOMBRE Y APELLIDOS': 'fullName',
  NOMBRE: 'fullName',
  NOMBRES: 'fullName',
  GENERO: 'gender',
  EMAIL: 'email',
  CORREO: 'email',
  'CORREO ELECTRONICO': 'email',
  CELULAR: 'phone',
  TELEFONO: 'phone',
  'CENTRO COSTOS': 'costCenter',
  'CENTRO DE COSTOS': 'costCenter',
  CIUDAD: 'city',
  EMPLEADO: 'employeeStatus',
  ESTADO: 'employeeStatus',
  SALARIO: 'salary',
  CONTRATACION: 'contractType',
  IP: 'expectedIp',
  SEDE: 'sedes',
  SEDES: 'sedes',
  OFICINA: 'sedes',
  OFICINAS: 'sedes',
};
const REQUIRED: { field: keyof ImportRowInput; label: string }[] = [
  { field: 'documentNumber', label: 'CC' },
  { field: 'fullName', label: 'NOMBRE Y APELLIDOS' },
  { field: 'email', label: 'EMAIL' },
];

const normalizeHeader = (h: unknown) =>
  String(h ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

/** Lee la primera hoja del Excel y devuelve las filas con los nombres de campo que entiende el servidor. */
export async function parseEmployeesFile(file: File): Promise<{ rows: ImportRowInput[]; ignoredColumns: string[] }> {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) throw new Error('El archivo no tiene hojas.');
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: null, blankrows: false });
  if (matrix.length < 2) throw new Error('El archivo no trae filas de empleados (solo el encabezado o está vacío).');

  const headers = (matrix[0] as unknown[]).map(normalizeHeader);
  const fieldAt: (keyof ImportRowInput | null)[] = headers.map((h) => HEADER_MAP[h] ?? null);
  const missing = REQUIRED.filter((r) => !fieldAt.includes(r.field)).map((r) => r.label);
  if (missing.length > 0) throw new Error(`Faltan columnas obligatorias en el encabezado: ${missing.join(', ')}. Descarga la plantilla para ver el formato.`);
  const ignoredColumns = headers.filter((h, i) => h && !fieldAt[i]);

  const rows: ImportRowInput[] = [];
  for (const line of matrix.slice(1)) {
    const cells = line as unknown[];
    if (cells.every((c) => c === null || String(c).trim() === '')) continue;
    const row: Record<string, unknown> = {};
    fieldAt.forEach((field, i) => {
      if (field) row[field] = cells[i] ?? null;
    });
    rows.push(row as ImportRowInput);
  }
  if (rows.length === 0) throw new Error('El archivo no trae filas de empleados.');
  return { rows, ignoredColumns };
}

export function downloadEmployeesTemplate() {
  const header = ['ITEM', 'CC', 'PROCESO', 'NOMBRE Y APELLIDOS', 'GENERO', 'EMAIL', 'CELULAR', 'CENTRO COSTOS', 'CIUDAD', 'EMPLEADO', 'SALARIO', 'CONTRATACION', 'IP', 'SEDES'];
  const example = [1, 1000000001, 'TG', 'Nombre Apellido de Ejemplo', 'F', 'ejemplo@correo.com', 3001234567, 'Torre vnp', 'MEDELLIN', 'ACTIVO', 1750905, 'OSYA', '', 'Oficina Coltabaco - Nutibara'];
  const ws = XLSX.utils.aoa_to_sheet([header, example]);
  ws['!cols'] = header.map((h) => ({ wch: Math.max(h.length + 2, 14) }));
  const notes = XLSX.utils.aoa_to_sheet([
    ['Cómo llenar la plantilla'],
    ['Obligatorias: CC, NOMBRE Y APELLIDOS y EMAIL (con el correo inicia sesión).'],
    ['EMPLEADO: ACTIVO. Las filas con otro estado no reciben cuenta.'],
    ['Si el correo ya existe en la plataforma, no se crea cuenta ni se cambia su clave: solo se vincula a TurnoGeo y se actualiza su ficha.'],
    ['SEDES (opcional): nombre de la oficina tal como está en Configuración > Redes de oficina; varias separadas por punto y coma. Vacío = remoto, pendiente de definir.'],
    ['Borra la fila de ejemplo antes de cargar.'],
  ]);
  notes['!cols'] = [{ wch: 110 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Empleados');
  XLSX.utils.book_append_sheet(wb, notes, 'Instrucciones');
  XLSX.writeFile(wb, 'plantilla_carga_empleados.xlsx');
}

/** Excel de resultados: hoja de claves iniciales (para entregar) y hoja con el resultado de cada fila. */
export function downloadImportResults(results: ImportResultRow[]) {
  const created = results.filter((r) => r.outcome === 'CREADO');
  const claves = [
    ['ITEM', 'CC', 'NOMBRE', 'CORREO (usuario)', 'CLAVE INICIAL'],
    ...created.map((r) => [r.item ?? '', r.documentNumber, r.fullName, r.email, r.initialPassword ?? '']),
    [],
    ['Entrega cada clave solo a su dueño. Las claves también quedan guardadas cifradas: se consultan con el ojito en Configuración > Acceso y usuarios.'],
  ];
  const wsClaves = XLSX.utils.aoa_to_sheet(claves);
  wsClaves['!cols'] = [{ wch: 7 }, { wch: 14 }, { wch: 38 }, { wch: 36 }, { wch: 22 }];

  const detalle = [
    ['ITEM', 'CC', 'NOMBRE', 'CORREO', 'RESULTADO', 'DETALLE'],
    ...results.map((r) => [r.item ?? '', r.documentNumber, r.fullName, r.email, r.outcome, r.message]),
  ];
  const wsDetalle = XLSX.utils.aoa_to_sheet(detalle);
  wsDetalle['!cols'] = [{ wch: 7 }, { wch: 14 }, { wch: 38 }, { wch: 36 }, { wch: 12 }, { wch: 90 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsClaves, 'Claves iniciales');
  XLSX.utils.book_append_sheet(wb, wsDetalle, 'Resultado por fila');
  const stamp = new Date().toLocaleString('sv-SE', { timeZone: 'America/Bogota' }).replace(/[: ]/g, '-').slice(0, 16);
  XLSX.writeFile(wb, `carga_empleados_resultado_${stamp}.xlsx`);
}
